import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import GenericCard from "components/UI/GenericCard/GenericCard";
import AntTable from "components/UI/AntTable";
import dayjs from "dayjs";
import {
  calcProfitPerc,
  formatMomentDuration,
  returnUpDownClassname,
} from "utils/helpers";
import TriggerSellPosition from "components/Strategies/Modals/TriggerSellPosition";
import ConfirmSellPosition from "components/Strategies/Modals/ConfirmSellPosition";
import { apiGetUserStrategies } from "api/apiStrategies";
import { useTradingRefresh } from "components/Trading/tradingRefresh";
import StrategyLink from "components/Trading/StrategyLink";
import LiveRateFreshness from "components/Trading/LiveRateFreshness";
import DashedTooltip from "components/UI/DashedTooltip";
import { Tag, Tooltip } from "antd";

// open positions that aren't plain "active" (see OPEN_POSITION_STATUSES)
const STATUS_TAGS = {
  "pending-close": { color: "gold", text: "Exit advised" },
  "close-in-progress": { color: "processing", text: "Closing" },
  "automated-close-failed": {
    color: "red",
    text: "Close failed",
    tooltip: "AutoTrader couldn't close this position; close it yourself",
  },
};

const OpenPositions = () => {
  const [openPositions, setOpenPositions] = useState([]);
  const accountType = useSelector((state) => state.main.accountType);
  const { refreshKey, notifyChanged } = useTradingRefresh();

  const getUserStrategies = async () => {
    const response = await apiGetUserStrategies({
      accountType,
    });
    const positions = response.flatMap((strategy) =>
      strategy.openPositions.map((position) => ({
        ...position,
        strategyName: strategy.name,
      })),
    );
    setOpenPositions(positions);
  };

  const sortByNumber = useCallback((a, b) => {
    // handle null / N/A values
    if (a === b) return 0;
    if (a === null || a === "N/A") return -1;
    if (b === null || b === "N/A") return 1;
    return a > b ? 1 : -1;
  }, []);

  const sortByText = useCallback((a, b) => {
    return a.toLowerCase().localeCompare(b.toLowerCase());
  }, []);

  const columns = [
    {
      title: "Symbol",
      dataIndex: "symbol",
      render: (symbol, position) => {
        const tag = STATUS_TAGS[position.orderStatus];
        return (
          <>
            {symbol}
            {tag && (
              <Tooltip title={tag.tooltip}>
                <Tag color={tag.color} style={{ marginInlineStart: 8 }}>
                  {tag.text}
                </Tag>
              </Tooltip>
            )}
          </>
        );
      },
      sorter: {
        compare: (a, b) => sortByText(a.symbol, b.symbol),
      },
    },
    {
      title: "Strategy",
      dataIndex: "strategyName",
      render: (name, position) => (
        <StrategyLink
          strategyId={position.strategy}
          name={name}
          instrument="stocks"
        />
      ),
      sorter: {
        compare: (a, b) => sortByText(a.strategyName, b.strategyName),
      },
    },
    {
      title: "Contracts",
      dataIndex: "amount",
      sorter: {
        compare: (a, b) => sortByNumber(a.amount, b.amount),
      },
    },
    {
      title: "Buy Price",
      dataIndex: "openRate",
      render: (text, properties) => (
        <div>
          <div>{text}</div>
          <div>
            <span style={{ fontSize: 12 }}>
              ${properties.openValue.toLocaleString()}
            </span>
          </div>
        </div>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.openRate, b.openRate),
      },
    },
    {
      title: "Current Rate",
      dataIndex: "currentRate",
      render: (text, properties) => {
        const change = calcProfitPerc(properties.openRate, text);
        return (
          <LiveRateFreshness
            lastLiveRateUpdate={properties.lastLiveRateUpdate}
          >
            <div>{text}</div>
            <div>
              <span
                style={{ fontSize: 12 }}
                className={returnUpDownClassname(change)}
              >
                {change}%
              </span>
            </div>
          </LiveRateFreshness>
        );
      },
      sorter: {
        compare: (a, b) => sortByNumber(a.currentRate, b.currentRate),
      },
    },
    {
      title: "Max Rate",
      dataIndex: "maxLiveRate",
      render: (text, properties) => {
        const change = calcProfitPerc(properties.openRate, text);
        return (
          <div>
            <div>{text}</div>
            <div>
              <span
                style={{ fontSize: 12 }}
                className={returnUpDownClassname(change)}
              >
                {change}%
              </span>
            </div>
          </div>
        );
      },
      sorter: {
        compare: (a, b) => sortByNumber(a.maxLiveRate, b.maxLiveRate),
      },
    },
    {
      title: "Min Rate",
      dataIndex: "minLiveRate",
      render: (text, properties) => {
        const change = calcProfitPerc(properties.openRate, text);
        return (
          <div>
            <div>{text}</div>
            <div>
              <span
                style={{ fontSize: 12 }}
                className={returnUpDownClassname(change)}
              >
                {change}%
              </span>
            </div>
          </div>
        );
      },
      sorter: {
        compare: (a, b) => sortByNumber(a.minLiveRate, b.minLiveRate),
      },
    },
    {
      title: "Profit",
      dataIndex: "profit.value",
      render: (text, properties) => (
        <span className={returnUpDownClassname(properties.profit.value)}>
          ${properties.profit.value.toLocaleString()}
        </span>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.profit.value, b.profit.value),
      },
    },
    {
      title: "Change",
      dataIndex: "profit.perc",
      render: (text, properties) => (
        <span className={returnUpDownClassname(properties.profit.perc)}>
          {properties.profit.perc}%
        </span>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.profit.perc, b.profit.perc),
      },
    },
    {
      title: (
        <DashedTooltip text="Highest profit calculated during regular trading hours">
          Max
        </DashedTooltip>
      ),
      dataIndex: "max.perc",
      render: (text, properties) => (
        <div
          className={returnUpDownClassname(properties.max.value)}
          title={`Rate: ${properties.max.rate}`}
        >
          <div>${properties.max.value.toLocaleString()}</div>
          <span style={{ fontSize: 12 }}>%{properties.max.perc}</span>
        </div>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.max.value, b.max.value),
      },
    },
    {
      title: (
        <DashedTooltip text="Higest loss calculated during regular trading hours">
          Min
        </DashedTooltip>
      ),
      dataIndex: "drawdown.perc",
      render: (text, properties) => (
        <div
          className={returnUpDownClassname(properties.min.value)}
          title={`Rate: ${properties.min.rate}`}
        >
          <div>${properties.min.value.toLocaleString()}</div>
          <span style={{ fontSize: 12 }}>%{properties.min.perc}</span>
        </div>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.min.value, b.min.value),
      },
    },
    {
      title: (
        <DashedTooltip text="Highest profit calculated by close rate">
          Run-up
        </DashedTooltip>
      ),
      dataIndex: "runup.perc",
      render: (text, properties) => (
        <div
          className={returnUpDownClassname(properties.runup.value)}
          title={`Rate: ${properties.runup.rate}`}
        >
          <div>${properties.runup.value.toLocaleString()}</div>
          <span style={{ fontSize: 12 }}>%{properties.runup.perc}</span>
        </div>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.runup.value, b.runup.value),
      },
    },
    {
      title: (
        <DashedTooltip text="Highest loss calculated by close rate">
          Drawdown
        </DashedTooltip>
      ),
      dataIndex: "drawdown.perc",
      render: (text, properties) => (
        <div
          className={returnUpDownClassname(properties.drawdown.value)}
          title={`Rate: ${properties.drawdown.rate}`}
        >
          <div>${properties.drawdown.value.toLocaleString()}</div>
          <span style={{ fontSize: 12 }}>%{properties.drawdown.perc}</span>
        </div>
      ),
      sorter: {
        compare: (a, b) => sortByNumber(a.drawdown.value, b.drawdown.value),
      },
    },
    {
      title: "Bars",
      dataIndex: "dealDaysOpen",
      sorter: {
        compare: (a, b) => sortByNumber(a.dealDaysOpen, b.dealDaysOpen),
      },
    },
    {
      title: "Duration",
      dataIndex: "openDate",
      render: (text) => (
        <span>{formatMomentDuration(+new Date() - +new Date(text))}</span>
      ),
      sorter: {
        compare: (a, b) => sortByText(a.openDate, b.openDate),
      },
    },
    {
      title: "Open Date",
      dataIndex: "openDate",
      render: (text) => <>{dayjs(text).format("HH:mm:ss - DD/MM/YY")}</>,
      sorter: {
        compare: (a, b) => sortByNumber(a.openDate, b.openDate),
      },
    },
    {
      title: "Trigger Sell",
      key: "uid",
      dataIndex: "",
      render: (text) =>
        text.orderStatus === "active" ? (
          <TriggerSellPosition
            activePosition={text}
            getUserStrategies={notifyChanged}
          />
        ) : (
          <div className="w-[70px]"></div>
        ),
    },
    {
      title: "Sell",
      key: "uid",
      dataIndex: "",
      render: (text) => (
        <ConfirmSellPosition
          activePosition={text}
          getUserStrategies={notifyChanged}
        />
      ),
    },
  ];

  useEffect(() => {
    getUserStrategies();
  }, [accountType, refreshKey]);

  return (
    <div className="cards-wrapper">
      <GenericCard
        title="Open Positions"
        className="table-card"
        isCardOpenByDefault={true}
        maxHeight={610}
      >
        <AntTable rowKey="_id" dataSource={openPositions} columns={columns} />
      </GenericCard>
    </div>
  );
};

export default OpenPositions;
